import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { WorkflowTask, FileMetadata } from '../../types';
import LibraryBrowser from '../LibraryBrowser/LibraryBrowser';
import { getActiveSharePointDestinations } from '../../services/sharePointLibraryApi';

interface TaskStartDialogProps {
  open: boolean;
  task: WorkflowTask;
  onClose: () => void;
  onStart: (
    taskId: number,
    fileData: { siteId: string; driveId: string; itemId: string },
    emails: { revisionEmail?: string; approvalEmail?: string; reviewEmail?: string }
  ) => void;
  projectId?: number;
}

interface FileSelectionData {
  siteId: string;
  driveId: string;
  itemId: string;
  name: string;
}

export const TaskStartDialog = ({ open, task, onClose, onStart, projectId }: TaskStartDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<FileSelectionData | null>(null);
  const [revisionEmail, setRevisionEmail] = useState('');
  const [approvalEmail, setApprovalEmail] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [siteId, setSiteId] = useState<string | null>(null);
  const [libraryName, setLibraryName] = useState<string | null>(null);
  const [loadingDest, setLoadingDest] = useState(false);
  const [destError, setDestError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !projectId) {return;}
    let cancelled = false;
    setLoadingDest(true);
    setDestError(null);
    getActiveSharePointDestinations(projectId)
      .then((destinations) => {
        if (cancelled) {return;}
        const def = destinations.find((d) => d.isDefault) || destinations[0];
        if (!def) {
          setDestError('No SharePoint destination is configured');
          return;
        }
        setSiteId(def.siteId);
        setLibraryName(def.libraryName);
      })
      .catch((err) => {
        if (!cancelled) {
          setDestError(err?.message || 'Failed to load SharePoint destination');
        }
      })
      .finally(() => {
        if (!cancelled) {setLoadingDest(false);}
      });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileSelect = (file: FileMetadata) => {
    setSelectedFile({
      siteId: siteId || file.siteId || '',
      driveId: file.driveId || '',
      itemId: file.itemId,
      name: file.name,
    });
    setFileBrowserOpen(false);
  };

  const handleSubmit = () => {
    const newErrors: { [key: string]: string } = {};
    if (!selectedFile) {
      newErrors.file = 'Please select a file';
    }
    if (revisionEmail && !validateEmail(revisionEmail)) {
      newErrors.revisionEmail = 'Invalid email';
    }
    if (approvalEmail && !validateEmail(approvalEmail)) {
      newErrors.approvalEmail = 'Invalid email';
    }
    if (reviewEmail && !validateEmail(reviewEmail)) {
      newErrors.reviewEmail = 'Invalid email';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 || !selectedFile) {return;}
    onStart(
      task.taskId,
      {
        siteId: selectedFile.siteId,
        driveId: selectedFile.driveId,
        itemId: selectedFile.itemId,
      },
      {
        revisionEmail: revisionEmail || undefined,
        approvalEmail: approvalEmail || undefined,
        reviewEmail: reviewEmail || undefined,
      }
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Start Task: {task.name}</DialogTitle>
        <DialogContent>
          {destError && (
            <Alert severity="error" sx={{ mb: 2 }}>{destError}</Alert>
          )}
          {loadingDest && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setFileBrowserOpen(true)}
              disabled={!projectId || loadingDest}
            >
              {selectedFile ? `Selected: ${selectedFile.name}` : 'Select SharePoint File'}
            </Button>
            {errors.file && <Alert severity="error">{errors.file}</Alert>}
            <TextField
              label="Revision Email"
              value={revisionEmail}
              onChange={(e) => setRevisionEmail(e.target.value)}
              error={!!errors.revisionEmail}
              helperText={errors.revisionEmail}
              fullWidth
            />
            <TextField
              label="Approval Email"
              value={approvalEmail}
              onChange={(e) => setApprovalEmail(e.target.value)}
              error={!!errors.approvalEmail}
              helperText={errors.approvalEmail}
              fullWidth
            />
            <TextField
              label="Review Email"
              value={reviewEmail}
              onChange={(e) => setReviewEmail(e.target.value)}
              error={!!errors.reviewEmail}
              helperText={errors.reviewEmail}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!selectedFile}>
            Start
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fileBrowserOpen}
        onClose={() => setFileBrowserOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select SharePoint File</DialogTitle>
        <DialogContent>
          {projectId ? (
            <LibraryBrowser
              projectId={projectId}
              libraryName={libraryName || undefined}
              siteId={siteId || undefined}
              onFileSelect={handleFileSelect}
            />
          ) : (
            <Alert severity="warning">Project ID is required to browse SharePoint files.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileBrowserOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
